import { useContext } from 'react';

import { LayerContext } from 'state/LayerContext';
import { useGetSegmentInfoQuery } from 'state/apiSlice';
import { useAppSelector } from 'state/hooks';
import {
  selectComparison,
  selectComparisonEndDate,
  selectCurrentProjectInfo,
  selectSegmentId,
  selectTargetDate,
  selectTargetEndDate,
} from 'state/workflowSlice';

import { TravelTimeChartDataExpander } from 'features/chart/TravelTimeChartDataExpander';
import {
  formatTargetSeriesName,
  formatTypicalSeriesName,
  getFormattedTime,
} from 'features/chart/nrtChartCommon';
import { formatTime, isOnDay } from 'features/common/datetimeUtils';
import {
  ExportType,
  nrtCommonInfo,
  triggerCsvExport,
  useExportFilename,
} from 'features/common/exportUtils';
import { useRouteInfo } from 'features/common/hooks/useRouteInfo';
import { binEndFromTs } from 'features/common/slotsUtils';
import { formatDistance, formatSpeed } from 'features/common/utils';
import { useTargetDateMetrics } from 'features/workflow_traveltime/nrtTravelTimeCommon';

export function useTravelTimesExportData(skip?: boolean) {
  const comparison = useAppSelector(selectComparison);
  const comparisonEndDate = useAppSelector(selectComparisonEndDate);
  const targetDate = useAppSelector(selectTargetDate);
  const targetEndDate = useAppSelector(selectTargetEndDate);
  const segmentId = useAppSelector(selectSegmentId);
  const userProject = useAppSelector(selectCurrentProjectInfo);

  // cached so no need to memoize
  const { layer } = useContext(LayerContext);
  const { currentData: segmentData } = useGetSegmentInfoQuery(
    { layerId: layer, routeIds: [segmentId] },
    { skip: !layer || !segmentId || skip },
  );

  const { feature: routeFeature, savedRoute } = useRouteInfo(skip);
  const filename = useExportFilename(ExportType.Data);
  const segment = segmentData && segmentData?.segments[segmentId];

  // typicalData from REST api
  const { metrics, comparisonData } = useTargetDateMetrics(skip);

  const handleExport = () => {
    if (skip) return;
    const dataExpander = new TravelTimeChartDataExpander(targetDate);
    const comparisonExpander = new TravelTimeChartDataExpander(
      targetDate,
      comparison,
    );
    const data = dataExpander.expandTravelTimes(metrics);
    const expandedComparisonData =
      comparisonExpander.expandTravelTimes(comparisonData);

    const targetSeriesName = formatTargetSeriesName(
      targetDate,
      false,
      targetEndDate,
    );
    const typicalSeriesName = formatTypicalSeriesName(
      comparison,
      targetDate,
      userProject.uses_middle_east_day_types,
      false,
      undefined,
      true,
      comparisonEndDate,
    );
    const header = [
      'Time Start',
      'Time End',
      `${targetSeriesName} Traveltime (HH:MM:SS)`,
      `${typicalSeriesName} Traveltime (HH:MM:SS)`,
      `${targetSeriesName} Quality`,
      `${typicalSeriesName} Quality`,
    ];

    const csv = [
      `Name, ${savedRoute ? savedRoute.routeName : segment.properties.name}`,
      `ID, ${segmentId}`,
      ...nrtCommonInfo(
        targetDate,
        comparison,
        segment,
        userProject.uses_middle_east_day_types,
        targetEndDate,
        comparisonEndDate,
      ),
      `Route/Segment length, ${formatDistance(
        routeFeature.properties.route_length,
        userProject.uses_metric,
      )}`,
      segment.properties.freeflow_speed_mph
        ? `Freeflow speed, ${formatSpeed(
            segment.properties.freeflow_speed_mph,
            userProject.uses_metric,
          )}`
        : null,
      metrics.freeflow_traveltime
        ? `Freeflow traveltime, ${formatTime(metrics.freeflow_traveltime)}`
        : null,
      '',
      '',
      header.join(', '),
    ];

    const estimationCopy = {
      0: 'Full coverage',
      1: '70 - 90% coverage',
      2: 'Insufficent coverage',
    };

    for (let i = 0; i < data.length; i += 1) {
      const row = data[i];
      const compRow = expandedComparisonData[i];
      const time = row.x;
      const endtime = getFormattedTime(binEndFromTs(time, metrics.bin_size));
      if (isOnDay(time, targetDate)) {
        const csv_row = [
          getFormattedTime(time),
          endtime,
          formatTime(row.y),
          formatTime(compRow.y),
          estimationCopy[row.quality],
          estimationCopy[compRow.quality],
        ];

        csv.push(csv_row.join(', '));
      }
    }
    triggerCsvExport(filename, csv);
  };

  const isActive = !skip && !!metrics && !!comparisonData && !!routeFeature;

  return { handleExport, isActive };
}
